import { useAnalytics } from './useAnalytics'
import { useNuxtApp } from 'nuxt/app'
import { hasAnalyticsConsent } from './useAnalyticsConsent'

interface StartBlogEngagementTrackingOptions {
  pagePath: string
  pageType: 'listing' | 'article'
}

export function useBlogTelemetry() {
  const nuxtApp = useNuxtApp()
  const gtagCandidate = (nuxtApp as { $gtag?: (action: string, category: string, label?: string, value?: number) => void }).$gtag
  const gtag = typeof gtagCandidate === 'function'
    ? gtagCandidate
    : null

  const {
    emitBlogListingViewed,
    emitBlogArticleViewed,
    emitBlogArticleClicked,
    emitBlogPaginationClicked,
    emitBlogScrollDepth,
    emitBlogReadTime,
  } = useAnalytics()

  function emitGa4(eventName: string, category: string, label: string, value?: number): void {
    if (!gtag) return
    gtag(eventName, category, label, value)
  }

  function trackBlogListingViewed(
    pagePath: string,
    pageTitle: string,
    articleCount: number,
    currentPage: number,
  ): void {
    if (!hasAnalyticsConsent()) return
    emitBlogListingViewed(pagePath, pageTitle, articleCount, currentPage)
    emitGa4('blog_listing_viewed', 'blog', 'listing-page', currentPage)
  }

  function trackBlogArticleViewed(
    pagePath: string,
    articleSlug: string,
    articleTitle: string,
    categories: string[],
    readingTimeMinutes: number,
  ): void {
    if (!hasAnalyticsConsent()) return
    emitBlogArticleViewed(pagePath, articleSlug, articleTitle, categories, readingTimeMinutes)
    emitGa4('blog_article_viewed', 'blog', 'article-detail', readingTimeMinutes)
  }

  function trackBlogArticleClicked(
    articlePath: string,
    articleSlug: string,
    articleTitle: string,
    articlePosition: number,
    currentPage: number,
  ): void {
    if (!hasAnalyticsConsent()) return
    emitBlogArticleClicked(articlePath, articleSlug, articleTitle, articlePosition, currentPage)
    emitGa4('blog_article_clicked', 'blog', 'listing-article-click', articlePosition)
  }

  function trackBlogPaginationClicked(
    pagePath: string,
    fromPage: number,
    toPage: number,
    totalPages: number,
  ): void {
    if (!hasAnalyticsConsent()) return
    emitBlogPaginationClicked(pagePath, fromPage, toPage, totalPages)
    emitGa4('blog_pagination_clicked', 'blog', 'pagination-click', toPage)
  }

  function startBlogEngagementTracking(options: StartBlogEngagementTrackingOptions): () => void {
    if (typeof globalThis.window === 'undefined' || !hasAnalyticsConsent()) return () => {}

    let scrollDepthTracked = false
    let readTimeTracked = false

    const trackScrollDepth = (): void => {
      if (scrollDepthTracked) return
      const scrollTop = globalThis.scrollY || document.documentElement.scrollTop || 0
      const viewportHeight = globalThis.innerHeight || document.documentElement.clientHeight || 0
      const scrollHeight = document.documentElement.scrollHeight || 0
      const maxScrollable = Math.max(scrollHeight - viewportHeight, 1)
      const percent = ((scrollTop + viewportHeight) / (maxScrollable + viewportHeight)) * 100

      if (percent >= 50) {
        if (!hasAnalyticsConsent()) return
        scrollDepthTracked = true
        emitBlogScrollDepth(options.pagePath, options.pageType)
        emitGa4('blog_scroll_depth', 'blog', `${options.pageType}-50pct`, 50)
        globalThis.removeEventListener('scroll', trackScrollDepth)
      }
    }

    const readTimeTimeout = globalThis.setTimeout(() => {
      if (readTimeTracked) return
      if (!hasAnalyticsConsent()) return
      readTimeTracked = true
      emitBlogReadTime(options.pagePath, options.pageType)
      emitGa4('blog_read_time', 'blog', `${options.pageType}-90s`, 90)
    }, 90000)

    globalThis.addEventListener('scroll', trackScrollDepth, { passive: true })
    trackScrollDepth()

    return () => {
      globalThis.removeEventListener('scroll', trackScrollDepth)
      globalThis.clearTimeout(readTimeTimeout)
    }
  }

  return {
    hasAnalyticsConsent,
    trackBlogListingViewed,
    trackBlogArticleViewed,
    trackBlogArticleClicked,
    trackBlogPaginationClicked,
    startBlogEngagementTracking,
  }
}
